import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  answerToId,
  firstQuestionId,
  getQuestion,
  type Answers,
  type AnswerValue,
} from "@/schema/quizSchema";
import { validateQuizDefinition } from "@/validation/validateQuizDefinition";
import { advancesImmediately, defaultAnswer } from "@/state/advanceMode";
import { getQuiz } from "@/quizzes/registry";
import { initialState, quizReducer, type QuizState } from "@/state/quizReducer";
import { QuestionView } from "@/components/QuestionView";
import { Hero } from "@/components/Hero";
import { Progress } from "@/components/Progress";
import { BuildingScreen } from "@/components/BuildingScreen";
import { ResultScreen } from "@/components/ResultScreen";
import { ErrorBox } from "@/components/ErrorBox";
import { LocaleContext } from "@/i18n/LocaleContext";
import { STRINGS } from "@/i18n/strings";
import { resolveSessionId } from "@/state/session";

export interface SubmissionPayload {
  quizSlug: string;
  sessionId: string;
  persona: string;
  answers: Answers;
}

export interface QuizEvent {
  quizSlug: string;
  sessionId: string;
  type: "start" | "view" | "answer" | "complete" | "result_view" | "cta_click";
  questionId?: string;
  answerId?: string;
}

const DEFAULT_BUILDING_MS = 1200;
/** How long a tapped single-choice option shows its selected fill before the
 *  quiz advances. Long enough to register as a response, short enough not to
 *  read as lag. */
export const DEFAULT_SELECT_HOLD_MS = 220;

export function QuizWidget({
  slug,
  buildingMs = DEFAULT_BUILDING_MS,
  selectHoldMs = DEFAULT_SELECT_HOLD_MS,
  onSubmit = () => {},
  onEvent = () => {},
}: {
  slug: string;
  buildingMs?: number;
  /** 0 disables the tap-feedback hold (single-choice answers advance synchronously). */
  selectHoldMs?: number;
  onSubmit?: (payload: SubmissionPayload) => void;
  onEvent?: (event: QuizEvent) => void;
}) {
  const quiz = getQuiz(slug);
  // Persisted per slug in sessionStorage: a mid-quiz refresh continues the
  // SAME analytics session (dedup happens server-side via distinct-session
  // counting) instead of registering a phantom drop-off + fresh visitor.
  const [sessionId] = useState(() => resolveSessionId(slug));

  // Boot-time quiz validation through the single validity seam (ADR-0007):
  // structural (Zod) always, behavioural (walker) only in dev. Memoized so it
  // runs once per quiz. The walker is an authoring/CI check (see
  // validateQuizDefinition's `behavioural` doc comment) — running it in
  // production would block the main thread for up to ~2s on real quizzes, so
  // production boot only runs the cheap structural Zod parse.
  const validity = useMemo(() => {
    if (!quiz) return { ok: false as const, message: `Unknown quiz: "${slug}"` };
    const result = validateQuizDefinition(quiz, { behavioural: import.meta.env.DEV });
    return result.ok
      ? { ok: true as const }
      : { ok: false as const, message: "This quiz is misconfigured." };
  }, [quiz, slug]);

  const [state, dispatch] = useReducer(
    quizReducer,
    quiz ?? null,
    (q): QuizState =>
      q ? initialState(q) : { phase: "error", message: "no quiz", answers: {}, previousPhase: "running" },
  );

  // Stable across re-renders so a re-render during the building phase does not restart
  // BuildingRows/ApplyingPage's onDone-keyed timer effect (they key their setTimeout on
  // identity of `onDone`).
  const done = useCallback(() => {
    onEvent({ quizSlug: quiz?.slug ?? slug, sessionId, type: "result_view" });
    dispatch({ type: "completed" });
  }, [onEvent, quiz, slug, sessionId]);

  // Emit a single "start" event once the quiz boots (deferred from Phase 0b).
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current || !quiz || !validity.ok) return;
    startedRef.current = true;
    onEvent({ quizSlug: quiz.slug, sessionId, type: "start" });
  }, [quiz, validity.ok, sessionId, onEvent]);

  // Emit a "view" event the first time each question is shown (feeds the funnel).
  const runningQuestionId = state.phase === "running" ? state.currentQuestionId : null;
  const viewedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!quiz || !validity.ok || runningQuestionId === null) return;
    if (viewedRef.current.has(runningQuestionId)) return;
    viewedRef.current.add(runningQuestionId);
    onEvent({ quizSlug: quiz.slug, sessionId, type: "view", questionId: runningQuestionId });
  }, [quiz, validity.ok, sessionId, onEvent, runningQuestionId]);

  // Single-choice tap feedback: the tapped option paints its selected state
  // (aria-pressed → accent fill) for a beat BEFORE the screen advances. Without
  // the hold the advance is synchronous, the screen remounts, and the user never
  // sees their tap register. One timer at a time; cleared on unmount.
  const holdRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (holdRef.current !== null) clearTimeout(holdRef.current);
    },
    [],
  );

  if (!quiz || !validity.ok) {
    return <ErrorBox message={validity.ok ? "Quiz unavailable." : validity.message} onRetry={() => {}} />;
  }

  const strings = STRINGS[quiz.meta.locale ?? "en"];

  // Per-quiz CSS, injected once and rendered in EVERY phase below (running /
  // building / result). Home for styles shared across a quiz's custom surfaces:
  // injecting inside the lazy result funnel alone leaves the building screen
  // unstyled (that bundle has not loaded yet). See QuizDefinition.styles.
  const quizStyles = quiz.styles ? <style>{quiz.styles}</style> : null;

  const emit = (event: Omit<QuizEvent, "quizSlug" | "sessionId">) =>
    onEvent({ quizSlug: quiz.slug, sessionId, ...event });

  const handleAnswer = (questionId: string, value: AnswerValue) => {
    if (state.phase !== "running") return;
    const answers: Answers = { ...state.answers, [questionId]: value };
    // Free-text answers are PII: the answer event records THAT the question
    // was answered, never the words (the API scrubs as a second layer).
    const isText = getQuestion(quiz, questionId)?.type === "text";
    emit({ type: "answer", questionId, ...(isText ? {} : { answerId: answerToId(value) }) });

    const next = quiz.branching(answers, questionId);
    if (next === null) {
      const persona = quiz.classifier(answers);
      emit({ type: "complete" });
      onSubmit({ quizSlug: quiz.slug, sessionId, persona, answers });
      dispatch({ type: "complete-questions", answers, persona });
    } else {
      dispatch({ type: "answer", answers, nextQuestionId: next });
    }
  };

  if (state.phase === "building") {
    const custom = quiz.renderBuilding?.({ answers: state.answers, onDone: done });
    return (
      <LocaleContext.Provider value={strings}>
        {quizStyles}
        <div className="qk-shell" style={{ padding: "var(--screen-pad)", maxWidth: "var(--max-content)", margin: "0 auto" }}>
          <div className="qk-stage">
            <div className="qk-screen">
              {custom ?? <BuildingScreen durationMs={buildingMs} onDone={done} />}
            </div>
          </div>
        </div>
      </LocaleContext.Provider>
    );
  }

  if (state.phase === "result") {
    const persona = quiz.personas.find((p) => p.key === state.persona);
    // One CTA-click signal for every result surface, default or custom. Custom
    // funnels call it from whatever "go to offer" moment they own.
    const trackCta = () => emit({ type: "cta_click" });
    const custom =
      quiz.renderResult && persona
        ? quiz.renderResult({ answers: state.answers, persona, trackCta })
        : null;
    return (
      <LocaleContext.Provider value={strings}>
        {quizStyles}
        <div className="qk-shell" style={{ padding: "var(--screen-pad)", maxWidth: "var(--max-content)", margin: "0 auto" }}>
          <div className="qk-stage">
            <div className="qk-screen">
              {custom ?? (persona ? <ResultScreen persona={persona} onCtaClick={trackCta} /> : <ErrorBox message="This quiz is misconfigured." onRetry={() => {}} />)}
            </div>
          </div>
        </div>
      </LocaleContext.Provider>
    );
  }

  if (state.phase === "error") {
    return (
      <div className="qk-shell" style={{ padding: "var(--screen-pad)", maxWidth: "var(--max-content)", margin: "0 auto" }}>
        <div className="qk-stage">
          <div className="qk-screen">
            <ErrorBox
              message={state.message}
              onRetry={() => dispatch({ type: "retry", firstQuestionId: firstQuestionId(quiz) })}
            />
          </div>
        </div>
      </div>
    );
  }

  // running
  const question = getQuestion(quiz, state.currentQuestionId);
  if (!question) {
    return <ErrorBox message="This quiz is misconfigured." onRetry={() => {}} />;
  }
  const index = quiz.questions.findIndex((q) => q.id === question.id);
  const draft = state.answers[question.id];

  // Gate the staged Next control on a multi question's `min` selections. With the
  // default min:0 (and existing quizzes) this is always false → Next never disabled
  // and existing behaviour is unchanged.
  const nextDisabled =
    question.type === "multi" &&
    (Array.isArray(draft) ? draft.length : 0) < (question.min ?? 0);

  return (
    <LocaleContext.Provider value={strings}>
      {quizStyles}
      <div className="qk-shell" style={{ padding: "var(--screen-pad)", maxWidth: "var(--max-content)", margin: "0 auto" }}>
        <div className="qk-progress">
          <Progress current={state.step} total={quiz.meta.progressTotal ?? quiz.questions.length} />
        </div>
        <div className="qk-stage">
          <div className="qk-screen" key={question.id} style={{ width: "100%" }}>
            {/* Landing hero above the first question (opt-in via meta.testimonial). */}
            {index === 0 && quiz.meta.testimonial && <Hero meta={quiz.meta} />}
            <QuestionView
              question={question}
              value={draft}
              onAnswer={(value) => {
                // Advance mode is driven by one table (ADVANCE_MODE): immediate
                // types (single, info) advance on answer; staged types stage a
                // draft and advance via the Next control below.
                if (advancesImmediately(question)) {
                  if (question.type === "single" && selectHoldMs > 0) {
                    if (holdRef.current !== null) return; // a tap is already in flight
                    // Stage the pick on the SAME question so aria-pressed paints,
                    // then advance once the hold elapses.
                    dispatch({ type: "answer", answers: { ...state.answers, [question.id]: value }, nextQuestionId: question.id });
                    holdRef.current = window.setTimeout(() => {
                      holdRef.current = null;
                      handleAnswer(question.id, value);
                    }, selectHoldMs);
                  } else {
                    handleAnswer(question.id, value);
                  }
                } else {
                  dispatch({ type: "answer", answers: { ...state.answers, [question.id]: value }, nextQuestionId: question.id });
                }
              }}
            />
            {!advancesImmediately(question) && (
              <button
                type="button"
                className="qk-cta"
                disabled={nextDisabled}
                onClick={() => handleAnswer(question.id, draft ?? defaultAnswer(question))}
              >
                {strings.next}
              </button>
            )}
          </div>
        </div>
      </div>
    </LocaleContext.Provider>
  );
}
